/**
 * Scenario Presets — investigation shortcuts for the Events filter sidebar.
 *
 * Each scenario defines a set of filter overrides that get applied as a group.
 * To add or tweak a scenario, edit the SCENARIOS array below.
 *
 * Filter fields use the FilterPanelState shape:
 *   - Tristate booleans: true = "with", false = "without", null = "all"
 *   - minScore / maxScore: 0–100 (0 and 100 are the defaults)
 *
 * The `filters` object only needs the fields the scenario changes.
 * Omitted fields are left untouched when applied, and reset to defaults when cleared.
 */

import type { FilterPanelState } from "@/types";
import { DEFAULT_FILTERS } from "@/types";

export interface ScenarioConfig {
  /** Display label */
  readonly label: string;
  /** Short description shown below the label */
  readonly description: string;
  /** MUI icon name — mapped to a React element in ScenariosPanel */
  readonly icon: string;
  /** Filter overrides applied when the scenario is activated */
  readonly filters: Partial<FilterPanelState>;
}

export const SCENARIOS: ScenarioConfig[] = [
  {
    label: "Leaked Credentials",
    description:
      "2FA failures with no successful logins — credentials may be in circulation.",
    icon: "VpnKeyOff",
    filters: {
      hasFailures: true,
      hasFailedSecondFactor: true,
      hasSuccessfulAuth: false,
    },
  },
  {
    label: "Compromised Usernames",
    description:
      "Invalid password failures — attacker knows the username but not the password.",
    icon: "PersonSearch",
    filters: {
      hasFailures: true,
      hasInvalidPassword: true,
      hasSuccessfulAuth: false,
    },
  },
];

/** Apply a scenario's filter overrides onto the current panel state. */
export function applyScenario(current: FilterPanelState, scenario: ScenarioConfig): FilterPanelState {
  return { ...current, ...scenario.filters };
}

/** Check whether all of a scenario's filter values are currently active. */
export function isScenarioActive(current: FilterPanelState, scenario: ScenarioConfig): boolean {
  return Object.entries(scenario.filters).every(
    ([key, value]) => current[key as keyof FilterPanelState] === value,
  );
}

/** Reset only the fields a scenario changed back to their defaults. */
export function resetScenario(current: FilterPanelState, scenario: ScenarioConfig): FilterPanelState {
  const patch: Partial<FilterPanelState> = {};
  for (const key of Object.keys(scenario.filters)) {
    const k = key as keyof FilterPanelState;
    (patch as Record<string, unknown>)[k] = DEFAULT_FILTERS[k];
  }
  return { ...current, ...patch };
}
